import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {SharedListState} from '../../interfaces/shared-list.interface';
import {
  Selector,
  SetState,
  SharedListAction,
  sharedListReducer,
  ToggleVehicle,
  ToggleAllVehicles,
  UnToggleAllVehicles
} from './shared-list-reducer';
import {map, scan, shareReplay, tap} from 'rxjs/operators';
import {Store} from '@ngxs/store';
import { ClearAllSelectedSharedListVehicles, SharedListToggleState } from 'remarketing-angular-library';

@Injectable({
  providedIn: 'root'
})
export class SharedListToggleService {
  private _hasCheckboxActionOverridingScroll = false;

  set hasCheckboxActionOverridingScroll(value: boolean) {
    this._hasCheckboxActionOverridingScroll = value;
  }

  get hasCheckboxActionOverridingScroll() {
    return this._hasCheckboxActionOverridingScroll;
  }

  public makeSelectAllCheckboxActive: Subject<boolean> = new Subject<boolean>();

  private static readonly initialState: SharedListState = {
    selectedVehicles: [],
    selectAll: false
  };

  sharedListAction = new BehaviorSubject<SharedListAction>(new SetState(SharedListToggleService.initialState));

  readonly sharedListState$: Observable<SharedListState> = this.sharedListAction.asObservable().pipe(
    scan(sharedListReducer, undefined),
    tap((state: SharedListState) => this.currentSharedListState.next(state)),
    shareReplay(1)
  );

  private readonly currentSharedListState = new BehaviorSubject<SharedListState>(undefined);

  constructor(
    private readonly _store: Store
  ) {
    this.sharedListState$.subscribe();
  }

  select<T>(selectFn: Selector<T>): Observable<T> {
    return this.sharedListState$.pipe(map(selectFn));
  }

  uncheckSelectAllCheckbox(): void {
    this.makeSelectAllCheckboxActive.next(false);
  }

  checkSelectAllCheckbox() {
    this.makeSelectAllCheckboxActive.next(true);
  }

  get getSelectAll() {
    return this.currentSharedListState.getValue().selectAll;
  }

  updateTheSelectAllStatus() {
    const value = this._store.selectSnapshot(SharedListToggleState.selectAll);
    if (!value) {
      this.uncheckSelectAllCheckbox();
    } else {
      this.checkSelectAllCheckbox();
    }
  }

  synchronizeSelectAll() {
    const value = this._store.selectSnapshot(SharedListToggleState.selectAll);
    if (value) {
      this.uncheckSelectAllCheckbox();
    } else {
      this.checkSelectAllCheckbox();
    }
  }

  vehicleToggle(vehicle: string) {
    this.sharedListAction.next(new ToggleVehicle(vehicle));
  }

  multipleVehicleToggle(vehicles: string[]) {
    this.sharedListAction.next(new ToggleAllVehicles(vehicles));
  }

  multipleVehicleUnToggle(vehicles: string[]) {
    this.sharedListAction.next(new UnToggleAllVehicles(vehicles));
  }

  snapShot() {
    return this.currentSharedListState.getValue();
  }

  setState(state: SharedListState) {
    this.sharedListAction.next(new SetState(state));
  }

  reset() {
    const initialState: SharedListState = {
      selectedVehicles: [],
      selectAll: false
    };
    this.uncheckSelectAllCheckbox();
    this.sharedListAction.next(new SetState(initialState));
    this._store.dispatch(new ClearAllSelectedSharedListVehicles());
  }
}
